import React, { useMemo, useState } from 'react'
import { DocumentSection } from '../../entities/Document'
import { StatusCards } from '../../features/StatusCards'
import { DocumentationMatrix } from '../../features/DocumentationMatrix'
import { Icon } from '../../shared/ui/Icon'
import { colors } from '../../shared/ui/colors'
import {
  WidgetContainer,
  WidgetHeader,
  WidgetTitle,
  WidgetDescription,
  ProjectSelectorContainer,
  LoaderContainer,
  LoaderSpinner,
  LoaderText,
  ErrorContainer,
  ErrorText
} from './DocumentationWidget.styles'
import { useGetObjects } from '../../api/documentation/hooks/useGetObjects'
import {
  ShortObjectDto,
  VitroDirectoryItemDto
} from '../../api/documentation/types/model'
import { useGetObjectInfo } from '../../api/documentation/hooks/useGetObjectInfo'
import { ProjectSelectorWithSearch } from '../../features/ProjectSelector/ProjectSelectorWithSearch'
import { useParams } from 'react-router-dom'

function mapItemToSection(
  item: VitroDirectoryItemDto,
  response: Record<string, VitroDirectoryItemDto[]>,
  level: number = 0,
  numberBase: string = '',
  n: number = 1
): DocumentSection {
  const hasChildren = !!response[item.id]
  const base = `${numberBase}${!!numberBase ? '.' : ''}`
  const nmb = `${base}${n}`
  return {
    id: item.id,
    projectId: item.projectLookup,
    sectionNumber: nmb,
    subsections: hasChildren
      ? response[item.id]?.map((item, i) =>
          mapItemToSection(item, response, level + 1, nmb, i + 1)
        )
      : [],
    title: item.name,
    code: item.sheetCetCipher,
    collapsed: true,
    level: level,
    status: item.status,
    stage: item.designStage,
    url: item.vitroUrl,
    original: item
  }
}

export const DocumentationWidget: React.FC = () => {
  const [selectedObject, setSelectedObject] = useState<ShortObjectDto | null>(
    null
  )
  const { livingComplexId } = useParams<{ livingComplexId: string }>()
  const { data: response } = useGetObjects(
    livingComplexId ? parseInt(livingComplexId) : undefined
  )
  const objectInfo = useGetObjectInfo(selectedObject?.guid) //TODO: Change to guid field
  const sections = useMemo<DocumentSection[]>(() => {
    if (!objectInfo.data || !objectInfo.data.data['root']) {
      return []
    }
    const result: DocumentSection[] = []
    let n = 1
    for (let root of objectInfo.data?.data['root']) {
      result.push(mapItemToSection(root, objectInfo.data.data, 0, '', n++))
    }
    return result
  }, [objectInfo.data])

  return (
    <WidgetContainer>
      <WidgetHeader>
        <div>
          <WidgetTitle>
            <Icon type="document" size={24} color={colors.gray900} />
            Документы
          </WidgetTitle>
          <WidgetDescription>
            Управление всеми типами проектной документации
          </WidgetDescription>
        </div>

        <ProjectSelectorContainer>
          {response && (
            <ProjectSelectorWithSearch
              selectedObject={selectedObject}
              onObjectChange={setSelectedObject}
              objects={response.objects}
            />
          )}
        </ProjectSelectorContainer>
      </WidgetHeader>
      {objectInfo.data && !objectInfo.isFetching && (
        <>
          <StatusCards
            receivedCount={objectInfo.data.recieved}
            expectedCount={objectInfo.data.pending}
            notProvidedCount={objectInfo.data.na}
          />

          <DocumentationMatrix
            sections={sections}
            projectName={selectedObject?.name || 'unset'}
          />
        </>
      )}

      {objectInfo.isFetching && (
        <LoaderContainer>
          <LoaderSpinner />
          <LoaderText>Загрузка данных...</LoaderText>
        </LoaderContainer>
      )}

      {selectedObject && !objectInfo.isFetching && !objectInfo.data && (
        <ErrorContainer>
          <ErrorText>
            Информация для объекта <b>{selectedObject.name}</b> не может быть
            получена.
          </ErrorText>
        </ErrorContainer>
      )}
    </WidgetContainer>
  )
}
